import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ActiveInactiveHotel,
  ActiveInactiveHotelContact,
  ActiveInactiveHotelRoomCategory,
  ActiveInactiveHotelSpecialRate,
  bulkUploadHotel,
  createHotel,
  createHotelContact,
  createHotelRoomCategory,
  createHotelSpecialRate,
  deleteHotel,
  deleteHotelContact,
  deleteHotelImage,
  deleteHotelRoomCategory,
  deleteHotelSpecialRate,
  getAllActiveHotelRoomCategory,
  getAllActiveHotels,
  getAllHotelContacts,
  getAllHotelRoomCategory,
  getAllHotels,
  getHotelByID,
  getHotelSpecialRate,
  updateHotel,
  updateHotelContact,
  updateHotelImages,
  updateHotelRoomCategory,
  updateHotelSpecialRate,
} from "./services/hotel.services";
import {
  IHotel,
  IHotelContactDetail,
  IHotelDetails,
  IHotelRoomCategoryDetail,
  IHotelState,
} from "./hotelModel";

const initialState: IHotelState = {
  loading: false,
  hotelImageLoading: false,
  hotelUploadLoading: false,
  hotelContactLoading: false,
  hotelRoomCategoryLoading: false,
  hotelSpecialRateLoading: false,
  error: "",
  HotelID: null,
  HotelImageID: null,
  HotelContactID: null,
  HotelRoomCategoryID: null,
  HotelSpecialRateID: null,
  HotelList: [],
  HotelDetail: {
    HotelName: "",
    Address: "",
    CityID: null,
    StateID: null,
    CountryID: null,
    HotelCategory: "",
    WeekDays: "",
    WeekEnds: "",
    IsActive: 1,
    HotelDescription: "",
    BookingURL: "",
    Notes: "",
    CancellationPolicy: "",
    URL: "",
    Bedroom: "",
    LivingArea: "",
    RoomAminities: "",
    BathroomFacilities: "",
    TotalRoom: null,
    SectorID: null,
    IsDisplay: false,
  },
  HotelContactDetail: [],
  HotelRoomCategoryDetail: [],
  ActiveHotelRoomCategoryList: [],
  HotelSpecialRateList: [],
  HotelSpecialRateDetails: {
    DateType: null,
    RoomCategoryID: null,
    FestivalID: null,
    FromDate: "",
    ToDate: "",

    MealID: null,
    MarkupAmount: null,
    MarkupType: null,
    SingleOccupancyRate: null,
    DoubleOccupancyRate: null,
    ExtraPersonRate: null,
    ChildWithBedRate: null,
    ChildWithoutBedRate: null,

    // SOEPAI: 0,
    // SOCPAI: 0,
    // SOMAPAI: 0,
    // SOAPAI: 0,

    // DOEPAI: 0,
    // DOCPAI: 0,
    // DOMAPAI: 0,
    // DOAPAI: 0,
    // CNBAge: 5,
    // CWBEPAI: 0,
    // CWBCPAI: 0,
    // CWBMAPAI: 0,
    // CWBAPAI: 0,
    // CNBEPAI: 0,
    // CNBCPAI: 0,
    // CNBMAPAI: 0,
    // CNBAPAI: 0,
    // EAEPAI: 0,
    // EACPAI: 0,
    // EAMAPAI: 0,
    // EAAPAI: 0,
    KitchenCharges: 0,
    IsActive: 1,
  },
  deleteIndex: -1,
  HotelImageDeleteIndex: -1,
};

const hotelSlice = createSlice({
  name: "hotel",
  initialState,
  reducers: {
    clearHotelDetails: (state) => {
      state.HotelDetail = initialState.HotelDetail;
    },
    setHotelID: (state, action) => {
      state.HotelID = action.payload;
    },
    clearHotelID: (state) => {
      state.HotelID = initialState.HotelID;
    },
    setHotelList: (state, action) => {
      state.HotelList = action.payload;
    },
    clearHotelList: (state) => {
      state.HotelList = initialState.HotelList;
    },
    setHotelImageID: (state, action) => {
      state.HotelImageID = action.payload;
    },
    clearHotelImageID: (state) => {
      state.HotelImageID = initialState.HotelImageID;
    },
    setHotelContactID: (state, action) => {
      state.HotelContactID = action.payload;
    },
    clearHotelContactID: (state) => {
      state.HotelContactID = initialState.HotelContactID;
    },
    setHotelRoomCategoryID: (state, action) => {
      state.HotelRoomCategoryID = action.payload;
    },
    clearHotelRoomCategoryID: (state) => {
      state.HotelRoomCategoryID = initialState.HotelRoomCategoryID;
    },
    setHotelSpecialRateID: (state, action) => {
      state.HotelSpecialRateID = action.payload;
    },
    clearHotelSpecialRateID: (state) => {
      state.HotelSpecialRateID = initialState.HotelSpecialRateID;
    },
    setHotelContactDeleteIndex: (state, action) => {
      state.deleteIndex = action.payload;
    },
    setHotelImageDeleteIndex: (state, action) => {
      state.HotelImageDeleteIndex = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllHotels.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllHotels.fulfilled,
      (state, action: PayloadAction<IHotel[]>) => {
        state.loading = false;
        state.HotelList = action.payload || [];
      }
    );
    builder.addCase(getAllHotels.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.HotelList = [];
    });
    builder.addCase(getAllActiveHotels.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveHotels.fulfilled,
      (state, action: PayloadAction<IHotel[]>) => {
        state.loading = false;
        state.HotelList = action.payload || [];
      }
    );
    builder.addCase(getAllActiveHotels.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.HotelList = [];
    });
    builder.addCase(createHotel.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(createHotel.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createHotel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateHotel.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(updateHotel.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateHotel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteHotel.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteHotel.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteHotel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getHotelByID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getHotelByID.fulfilled,
      (state, action: PayloadAction<IHotelDetails>) => {
        state.loading = false;
        state.HotelDetail = action.payload;
      }
    );
    builder.addCase(getHotelByID.rejected, (state, action) => {
      state.loading = false;
      state.HotelDetail = initialState.HotelDetail;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(ActiveInactiveHotel.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(ActiveInactiveHotel.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(ActiveInactiveHotel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Hotel Contact
    builder.addCase(getAllHotelContacts.pending, (state) => {
      state.hotelContactLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAllHotelContacts.fulfilled,
      (state, action: PayloadAction<IHotelContactDetail[]>) => {
        state.hotelContactLoading = false;
        state.HotelContactDetail = action.payload || [];
      }
    );
    builder.addCase(getAllHotelContacts.rejected, (state, action) => {
      state.hotelContactLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.HotelContactDetail = [];
    });
    builder.addCase(createHotelContact.pending, (state) => {
      state.hotelContactLoading = true;
      state.error = "";
    });
    builder.addCase(createHotelContact.fulfilled, (state) => {
      state.hotelContactLoading = false;
    });
    builder.addCase(createHotelContact.rejected, (state, action) => {
      state.hotelContactLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateHotelContact.pending, (state) => {
      state.hotelContactLoading = true;
      state.error = "";
    });
    builder.addCase(updateHotelContact.fulfilled, (state) => {
      state.hotelContactLoading = false;
    });
    builder.addCase(updateHotelContact.rejected, (state, action) => {
      state.hotelContactLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteHotelContact.pending, (state) => {
      state.hotelContactLoading = true;
      state.error = "";
    });
    builder.addCase(deleteHotelContact.fulfilled, (state) => {
      state.hotelContactLoading = false;
    });
    builder.addCase(deleteHotelContact.rejected, (state, action) => {
      state.hotelContactLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(ActiveInactiveHotelContact.pending, (state) => {
      state.hotelContactLoading = true;
      state.error = "";
    });
    builder.addCase(ActiveInactiveHotelContact.fulfilled, (state) => {
      state.hotelContactLoading = false;
    });
    builder.addCase(ActiveInactiveHotelContact.rejected, (state, action) => {
      state.hotelContactLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Hotel Room Category
    builder.addCase(getAllHotelRoomCategory.pending, (state) => {
      state.hotelRoomCategoryLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAllHotelRoomCategory.fulfilled,
      (state, action: PayloadAction<IHotelRoomCategoryDetail[]>) => {
        state.hotelRoomCategoryLoading = false;
        state.HotelRoomCategoryDetail = action.payload || [];
      }
    );
    builder.addCase(getAllHotelRoomCategory.rejected, (state, action) => {
      state.hotelRoomCategoryLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.HotelRoomCategoryDetail = [];
    });
    builder.addCase(getAllActiveHotelRoomCategory.pending, (state) => {
      state.hotelContactLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveHotelRoomCategory.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.hotelContactLoading = false;
        state.ActiveHotelRoomCategoryList = action.payload || [];
      }
    );
    builder.addCase(getAllActiveHotelRoomCategory.rejected, (state, action) => {
      state.hotelContactLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.ActiveHotelRoomCategoryList = [];
    });
    builder.addCase(createHotelRoomCategory.pending, (state) => {
      state.hotelRoomCategoryLoading = true;
      state.error = "";
    });
    builder.addCase(createHotelRoomCategory.fulfilled, (state) => {
      state.hotelRoomCategoryLoading = false;
    });
    builder.addCase(createHotelRoomCategory.rejected, (state, action) => {
      state.hotelRoomCategoryLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateHotelRoomCategory.pending, (state) => {
      state.hotelRoomCategoryLoading = true;
      state.error = "";
    });
    builder.addCase(updateHotelRoomCategory.fulfilled, (state) => {
      state.hotelRoomCategoryLoading = false;
    });
    builder.addCase(updateHotelRoomCategory.rejected, (state, action) => {
      state.hotelRoomCategoryLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteHotelRoomCategory.pending, (state) => {
      state.hotelRoomCategoryLoading = true;
      state.error = "";
    });
    builder.addCase(deleteHotelRoomCategory.fulfilled, (state) => {
      state.hotelRoomCategoryLoading = false;
    });
    builder.addCase(deleteHotelRoomCategory.rejected, (state, action) => {
      state.hotelRoomCategoryLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(ActiveInactiveHotelRoomCategory.pending, (state) => {
      state.hotelRoomCategoryLoading = true;
      state.error = "";
    });
    builder.addCase(ActiveInactiveHotelRoomCategory.fulfilled, (state) => {
      state.hotelRoomCategoryLoading = false;
    });
    builder.addCase(
      ActiveInactiveHotelRoomCategory.rejected,
      (state, action) => {
        state.hotelRoomCategoryLoading = false;
        state.error = action.error.message || "Something went wrong";
      }
    );

    // Hotel Special Rate
    // builder.addCase(getAllHotelSpecialRate.pending, (state) => {
    //   state.hotelSpecialRateLoading = true;
    //   state.error = "";
    // });
    // builder.addCase(
    //   getAllHotelSpecialRate.fulfilled,
    //   (state, action: PayloadAction<IHotelSpecialRateList[]>) => {
    //     state.hotelSpecialRateLoading = false;
    //     state.HotelSpecialRateList = action.payload || [];
    //   }
    // );
    // builder.addCase(getAllHotelSpecialRate.rejected, (state, action) => {
    //   state.hotelSpecialRateLoading = false;
    //   state.error = action.error.message || "Something went wrong";
    //   state.HotelSpecialRateList = [];
    // });
    builder.addCase(createHotelSpecialRate.pending, (state) => {
      state.hotelSpecialRateLoading = true;
      state.error = "";
    });
    builder.addCase(createHotelSpecialRate.fulfilled, (state) => {
      state.hotelSpecialRateLoading = false;
    });
    builder.addCase(createHotelSpecialRate.rejected, (state, action) => {
      state.hotelSpecialRateLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateHotelSpecialRate.pending, (state) => {
      state.hotelSpecialRateLoading = true;
      state.error = "";
    });
    builder.addCase(updateHotelSpecialRate.fulfilled, (state) => {
      state.hotelSpecialRateLoading = false;
    });
    builder.addCase(updateHotelSpecialRate.rejected, (state, action) => {
      state.hotelSpecialRateLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteHotelSpecialRate.pending, (state) => {
      state.hotelSpecialRateLoading = true;
      state.error = "";
    });
    builder.addCase(deleteHotelSpecialRate.fulfilled, (state) => {
      state.hotelSpecialRateLoading = false;
    });
    builder.addCase(deleteHotelSpecialRate.rejected, (state, action) => {
      state.hotelSpecialRateLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getHotelSpecialRate.pending, (state) => {
      state.hotelSpecialRateLoading = true;
      state.error = "";
    });
    builder.addCase(getHotelSpecialRate.fulfilled, (state) => {
      state.hotelSpecialRateLoading = false;
    });
    builder.addCase(getHotelSpecialRate.rejected, (state, action) => {
      state.hotelSpecialRateLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(ActiveInactiveHotelSpecialRate.pending, (state) => {
      state.hotelSpecialRateLoading = true;
      state.error = "";
    });
    builder.addCase(ActiveInactiveHotelSpecialRate.fulfilled, (state) => {
      state.hotelSpecialRateLoading = false;
    });
    builder.addCase(
      ActiveInactiveHotelSpecialRate.rejected,
      (state, action) => {
        state.hotelSpecialRateLoading = false;
        state.error = action.error.message || "Something went wrong";
      }
    );

    builder.addCase(bulkUploadHotel.pending, (state) => {
      state.hotelUploadLoading = true;
      state.error = "";
    });
    builder.addCase(bulkUploadHotel.fulfilled, (state) => {
      state.hotelUploadLoading = false;
    });
    builder.addCase(bulkUploadHotel.rejected, (state, action) => {
      state.hotelUploadLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateHotelImages.pending, (state) => {
      state.hotelImageLoading = true;
      state.error = "";
    });
    builder.addCase(updateHotelImages.fulfilled, (state) => {
      state.hotelImageLoading = false;
    });
    builder.addCase(updateHotelImages.rejected, (state, action) => {
      state.hotelImageLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteHotelImage.pending, (state) => {
      state.hotelImageLoading = true;
      state.error = "";
    });
    builder.addCase(deleteHotelImage.fulfilled, (state) => {
      state.hotelImageLoading = false;
    });
    builder.addCase(deleteHotelImage.rejected, (state, action) => {
      state.hotelImageLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearHotelDetails,
  setHotelID,
  clearHotelID,
  setHotelList,
  clearHotelList,
  setHotelImageID,
  clearHotelImageID,
  setHotelContactID,
  clearHotelContactID,
  setHotelRoomCategoryID,
  clearHotelRoomCategoryID,
  setHotelSpecialRateID,
  clearHotelSpecialRateID,
  setHotelContactDeleteIndex,
  setHotelImageDeleteIndex,
} = hotelSlice.actions;
export default hotelSlice.reducer;
